import React, { useState } from "react";
import { toast } from "sonner";
import {
  X, Spinner, Check, CalendarBlank, Phone, ChatCircleDots,
  Envelope, Users, House, Clock, MapPin,
} from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";

/**
 * ManualEventModal — alternativa testuale al microfono in Agenda.
 *
 * Permette all'agente di inserire manualmente un impegno (appuntamento,
 * chiamata, scadenza, ecc.) tramite un form classico, senza dover usare
 * la voce. Salva direttamente via POST /api/agenda/todos.
 */
const TYPE_OPTIONS = [
  { v: "promemoria",    l: "Promemoria",    icon: Clock },
  { v: "chiamata",      l: "Chiamata",      icon: Phone },
  { v: "visita",        l: "Visita",        icon: House },
  { v: "sopralluogo",   l: "Sopralluogo",   icon: MapPin },
  { v: "firma",         l: "Firma",         icon: Check },
  { v: "scadenza",      l: "Scadenza",      icon: CalendarBlank },
  { v: "riunione",      l: "Riunione",      icon: Users },
  { v: "comunicazione", l: "Comunicazione", icon: ChatCircleDots },
];

const CHANNEL_OPTIONS = [
  { v: "interno",  l: "Interno" },
  { v: "chiamata", l: "Chiamata" },
  { v: "whatsapp", l: "WhatsApp" },
  { v: "email",    l: "Email" },
  { v: "incontro", l: "Di persona" },
];

export default function ManualEventModal({ open, onClose, onCreated, contacts = [] }) {
  const [title, setTitle] = useState("");
  const [eventType, setEventType] = useState("promemoria");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [contactId, setContactId] = useState("");
  const [priority, setPriority] = useState("media");
  const [channel, setChannel] = useState("interno");
  const [location, setLocation] = useState("");
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTitle(""); setEventType("promemoria"); setDate(""); setTime("");
    setContactId(""); setPriority("media"); setChannel("interno");
    setLocation(""); setDetail("");
  };

  if (!open) return null;

  const canSubmit = title.trim().length >= 3 && !submitting;

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const due_at = date ? `${date}T${time || "09:00"}:00` : null;
      await api.post("/agenda/todos", {
        title: title.trim(),
        detail: detail.trim() || "",
        due_at,
        contact_id: contactId || null,
        priority,
        channel,
        event_type: eventType,
        location: location.trim() || null,
      });
      toast.success("Impegno aggiunto in agenda");
      reset();
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(apiError(err, "Errore salvataggio"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-3" data-testid="manual-event-modal">
      {/* iter 275: su mobile il modal ora è FULL-HEIGHT (era max-h-[92vh] che,
          combinato con la URL bar di Safari iOS, faceva scorrere via il
          titolo). Header sticky top-0. */}
      <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[92dvh] sm:h-auto sm:max-h-[92vh]">
        {/* Header — sticky così l'X resta sempre visibile anche scrollando */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 px-5 py-4 border-b border-[#E5E7E0] bg-gradient-to-br from-[#F5F4F0] to-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold">Nuovo impegno</div>
            <div className="text-[15px] font-semibold text-[#1C1C1A] mt-0.5">Scrivi tu, niente voce</div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white border border-[#E5E7E0] hover:border-[#1C1C1A] hover:bg-[#F1F4ED] flex items-center justify-center text-[#1C1C1A] flex-shrink-0 shadow-sm"
            data-testid="manual-event-close"
            aria-label="Chiudi"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Tipo */}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1.5">Tipo</label>
            <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="manual-event-type">
              {TYPE_OPTIONS.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    type="button"
                    key={t.v}
                    onClick={() => setEventType(t.v)}
                    className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11.5px] font-medium border transition-all ${eventType === t.v ? "bg-[#1C1C1A] text-white border-[#1C1C1A]" : "bg-white text-[#3F3D38] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                    data-testid={`manual-event-type-${t.v}`}
                  >
                    <Icon size={12} weight={eventType === t.v ? "fill" : "regular"} />
                    {t.l}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Titolo */}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1">Cosa devi fare</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="es. Richiamare Marco Ferrari per il trilocale"
              className="w-full px-3 py-2.5 bg-white border border-[#E5E7E0] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
              required
              autoFocus
              data-testid="manual-event-title"
            />
          </div>

          {/* Data + Ora */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1">Quando</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-[#E5E7E0] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                data-testid="manual-event-date"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1">Ora</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                disabled={!date}
                className="w-full px-3 py-2.5 bg-white border border-[#E5E7E0] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E] disabled:opacity-50"
                data-testid="manual-event-time"
              />
            </div>
          </div>

          {/* Contatto */}
          {contacts.length > 0 && (
            <div>
              <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1">
                Cliente <span className="text-[#9A968B] font-normal normal-case">(facoltativo)</span>
              </label>
              <select
                value={contactId}
                onChange={(e) => setContactId(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-[#E5E7E0] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                data-testid="manual-event-contact"
              >
                <option value="">— Nessun cliente —</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Canale + Priorità */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1">Canale</label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-[#E5E7E0] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                data-testid="manual-event-channel"
              >
                {CHANNEL_OPTIONS.map((c) => (
                  <option key={c.v} value={c.v}>{c.l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1">Priorità</label>
              <div className="flex gap-1" data-testid="manual-event-priority">
                {["bassa", "media", "alta"].map((p) => (
                  <button
                    type="button"
                    key={p}
                    onClick={() => setPriority(p)}
                    className={`flex-1 px-2 py-2 text-[11.5px] capitalize rounded-md border transition-all ${priority === p ? (p === "alta" ? "bg-red-600 text-white border-red-600" : p === "bassa" ? "bg-[#9A968B] text-white border-[#9A968B]" : "bg-[#7C8B6E] text-white border-[#7C8B6E]") : "bg-white text-[#3F3D38] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                    data-testid={`manual-event-priority-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Luogo */}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1">
              Luogo <span className="text-[#9A968B] font-normal normal-case">(facoltativo)</span>
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="es. Via Brera 12, Milano"
              className="w-full px-3 py-2.5 bg-white border border-[#E5E7E0] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
              data-testid="manual-event-location"
            />
          </div>

          {/* Note */}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1">
              Note <span className="text-[#9A968B] font-normal normal-case">(facoltativo)</span>
            </label>
            <textarea
              rows={2}
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder="Dettagli, link, info utili…"
              className="w-full px-3 py-2 bg-white border border-[#E5E7E0] rounded-md text-[13px] focus:outline-none focus:border-[#7C8B6E] resize-none"
              data-testid="manual-event-detail"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="border-t border-[#E5E7E0] px-5 py-3 bg-white flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-[12.5px] text-[#9A968B] hover:text-[#1C1C1A]"
            data-testid="manual-event-cancel"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 bg-[#7C8B6E] hover:bg-[#6B7A5D] disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-md text-[12.5px] transition-colors"
            data-testid="manual-event-submit"
          >
            {submitting ? <Spinner size={13} className="animate-spin" /> : <Check size={13} weight="bold" />}
            {submitting ? "Salvo…" : "Aggiungi in agenda"}
          </button>
        </div>
      </div>
    </div>
  );
}
